import type { BriefDetails, DeliverableDetails, Version } from '@mybusinessmate/schema';
import { formatDateTime, relativeTime } from './task-display';

/** "v2". Version 0 is the brief. */
export const versionBadge = (versionNumber: number) => `v${versionNumber}`;

const firstName = (name: string | null) => name?.split(' ')[0] ?? 'the lead';

/** What the version list says under a version's name, from where it stands (Figma A05.4 "Versions"). */
export function versionStateLine(version: Version, now: Date = new Date()): string {
  switch (version.internalStatus) {
    case 'draft':
      return `Draft · saved ${relativeTime(version.updatedAt, now)}`;
    case 'pending_lead_review':
      return `Lead QA · ${relativeTime(version.updatedAt, now)}`;
    case 'changes_requested':
      return `Changes asked by ${firstName(version.leadReviewerName)}`;
    case 'approved':
      if (version.clientStatus === 'approved') return 'Approved by the client';
      if (version.clientStatus === 'revision_requested') return 'Client asked for changes';
      return `Sent to client · ${relativeTime(version.publishedAt ?? version.updatedAt, now)}`;
  }
}

/** The line under "Version 2" in the detail pane: who, when, and whether the client can see it. */
export function versionSubtitle(version: Version): string {
  const by = version.authorName ?? 'the team';
  if (version.isPublished) {
    return `Approved by ${version.publishedByName ?? 'the lead'} · sent to client ${formatDateTime(
      version.publishedAt ?? version.updatedAt,
    )}`;
  }
  const when = formatDateTime(version.createdAt);
  return version.internalStatus === 'draft'
    ? `Draft by ${by} · ${when} · not sent to QA yet`
    : `Submitted by ${by} · ${when} · not visible to client`;
}

const caption = (details: BriefDetails | DeliverableDetails) =>
  'caption' in details && typeof details.caption === 'string' ? details.caption.trim() : '';

/**
 * How the copy compares with what was asked, for the chip beside "Caption" on the review page. Worked out here from
 * the two texts (the API stores no diff): shorter or longer by more than a tenth, otherwise nothing is said.
 */
export function captionChange(brief: BriefDetails, draft: DeliverableDetails): string | null {
  const asked = caption(brief);
  const sent = caption(draft);
  if (!asked || !sent || asked === sent) return null;
  const ratio = sent.length / asked.length;
  if (ratio < 0.9) return 'Shortened vs brief';
  if (ratio > 1.1) return 'Longer than brief';
  return 'Reworded vs brief';
}

/** The previous version, if the lead sent it back: its notes are what this one answers. */
export function answeredVersion(versions: Version[], current: Version): Version | null {
  const previous = versions.find((v) => v.versionNumber === current.versionNumber - 1);
  return previous && previous.internalStatus === 'changes_requested' ? previous : null;
}
